import { AfterViewChecked, AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { PastEssaySubmissionResponse } from '../../models/essay-pilot/pastEssaySubmissionResponse';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { EssayCommentResponse } from '../../models/essay-pilot/essayCommentResponse';
import { EssayPilotService } from '../../services/essay-pilot/essay-pilot.service';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { EssayCommentRequest } from '../../models/essay-pilot/essayCommentRequest';
import { MessageService } from 'primeng/api';
import { OverlayPanel, OverlayPanelModule } from 'primeng/overlaypanel';
import DOMPurify from 'dompurify';


@Component({
  selector: 'app-review-submission',
  standalone: true,
  imports: [ButtonModule, CommonModule, ReactiveFormsModule,OverlayPanelModule],
  templateUrl: './review-submission.component.html',
  styleUrl: './review-submission.component.scss'
})
export class ReviewSubmissionComponent implements OnChanges, AfterViewInit{

  @ViewChild('targetText') targetText: ElementRef | undefined;
  @ViewChild('addCommentButton') addCommentButton: ElementRef | undefined;
  @ViewChild('op') op: OverlayPanel | undefined;
  iconPosition: { top: string; left: string } = { top: '0', left: '0' };

  selectedSubmission: PastEssaySubmissionResponse | null = null;
  studentId: string = '';
  commentList: EssayCommentResponse[] = [];
  commentForm!: FormGroup;
  selectedText: string | undefined;
  submitted: boolean = false;
  isLoading: boolean = false;
  showAddCommentButton: boolean = false;
  newCommentRequest: EssayCommentRequest | undefined;
  totalWords: number = 0;
  activeCommentId: number | null = null;
  private initialized = false;
  // private addingNewComment: boolean = false;

  @Input() currentIndex: number = 0;
  @Input() submissions: PastEssaySubmissionResponse[] = [];
  @Output() closeReviewDialog = new EventEmitter();

  highlightComment(commentid: number){
    if(this.activeCommentId === commentid){
      this.activeCommentId = null;
    }else{
      this.activeCommentId = commentid;
    }
  }

  downloadEssay() {
    // Get the element containing the HTML answer
    const elementToPrint = document.getElementById('targetText');
    if (!elementToPrint) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Content not found to download.' });
      return;
    }

    // Prepare the question as H1 bold
    const question = this.selectedSubmission?.question
      ? `<h1 style='font-weight:bold;'>${this.escapeHtml(this.selectedSubmission.question)}</h1>`
      : '';

    // Prepare the HTML content for Word
    const htmlContent = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
        <head><meta charset='utf-8'><title>Essay Answer</title></head>
        <body>${question}${this.sanitizeHtml(this.selectedSubmission?.htmlAnswer)}</body>
      </html>
    `;

    // Create a Blob with the correct MIME type
    const blob = new Blob([htmlContent], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);

    // Create a temporary anchor to trigger download
    const a = document.createElement('a');
    const fileName = this.selectedSubmission?.question
      ? `${this.selectedSubmission.question.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 40)}.doc`
      : 'essay-answer.doc';
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  /**
   * Escapes HTML special characters in a string
   */
  private escapeHtml(text: string): string {
    return text.replace(/[&<>"']/g, function (c) {
      switch (c) {
        case '&': return '&amp;';
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '"': return '&quot;';
        case "'": return '&#39;';
        default: return c;
      }
    });
  }

  initializeForm(){
    this.commentForm = this.formBuilder.group({
      commentForm: ['', Validators.required]
    });
  }

  constructor(
    private route: ActivatedRoute,
    private essayPilotService: EssayPilotService,
    private formBuilder: FormBuilder,
    private messageService: MessageService,
    private renderer: Renderer2
  ) {}

  //Lifecycle hook
  ngAfterViewInit(): void {
    if(this.targetText){
      this.renderer.listen(this.targetText.nativeElement, 'mouseup', this.handleSection.bind(this));
    }
  }

  //Fills selectedText local variable, used further in new comment request
  //Timeout keeps add comment button from being visible after text is aselected already
  handleSection(event: MouseEvent) {
    setTimeout(() => {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();

        if (this.targetText && this.targetText.nativeElement.contains(range.commonAncestorContainer)) {
          this.selectedText = selection.toString().trim();

          if (this.selectedText.length > 0) {
            this.showAddCommentButton = true;

            // Get popup-relative position
            const popupRect = this.targetText.nativeElement.getBoundingClientRect();
            this.iconPosition = {
              top: `${rect.top - popupRect.top - 20}px`,
              left: `${rect.right - popupRect.left + 5}px`
            };
          } else {
            this.showAddCommentButton = false;
          }
        } else {
          this.showAddCommentButton = false;
        }
      } else {
        this.showAddCommentButton = false;
      }
    }, 50);
  }


  //Button function for new comment to be added
  addNewComment(event: any){
    if(this.op){
      this.op?.toggle(event);
    }

    if(this.selectedText && this.selectedSubmission?.id){
      this.newCommentRequest = {
        pastEssaySubmissionId: this.selectedSubmission.id,
        selectedText: this.selectedText,
        comment: ''
      };
    }
  }

  //Lifecycle hook
  ngOnChanges(changes: SimpleChanges): void {
    this.studentId = this.route.snapshot.paramMap.get('id') || '';
    if (changes['submissions'] && changes['submissions'].currentValue?.length > 0) {
      // Set index to last only once when first initializing
      if (!this.initialized) {
        this.currentIndex = this.submissions.length - 1;
        this.initialized = true;
      }
      this.updateSelectedSubmission();
    }
    this.listEssayComments();
    this.initializeForm();
  }



  private updateSelectedSubmission(): void {
    this.selectedSubmission = this.submissions.length > 0 ? this.submissions[this.currentIndex] : null;
  
    if (this.selectedSubmission?.answer) {
      this.totalWords = this.selectedSubmission.answer.trim().split(/\s+/).filter(word => word).length;
    } else {
      this.totalWords = 0;
    }
  }


  //Submitting new comment
  onSubmit(){
    this.submitted = true;
    this.isLoading = true;
    if(this.commentForm.invalid){
      this.isLoading = false;
      return;
    }
    if (this.newCommentRequest && this.commentForm.valid) {
      this.newCommentRequest.comment = this.commentForm.get('commentForm')?.value;
      this.essayPilotService.addEssayComment(this.newCommentRequest).subscribe({
        next: (response) =>{
          this.isLoading = false;
          this.commentList.push(response);
          this.messageService.add({ severity: "success", summary: "Success", detail: "Comment updated successfully!" });
          this.resetForm();
        },
        error: (err) => {
          this.isLoading = false;
          console.warn(`error while retriving submitting comment:${err}`)
        }
      })
    }
    this.op?.hide();
    this.selectedText = undefined;
    this.showAddCommentButton = false;
    this.isLoading = false;
  }

  //Displaying past comments
  listEssayComments(){
    this.essayPilotService.getEssayComments({
      pastEssaySubmissionId:this.selectedSubmission?.id,
      status: 0 //should show both pending(0) and resolved(1) comments
    }).subscribe({
      next:(response) =>{
        this.commentList = response.result;
      },
      error:(err) => {
        console.warn(`error while retriving essay comment list:${err}`)
      }
    })
  }

  deleteEssayComment(commentId: number){
    this.isLoading = true;
    this.essayPilotService.deleteEssayComment(commentId).subscribe({
      next: (response) => {
        this.isLoading = false;
        const index = this.commentList.findIndex(comment => comment.id === commentId);
        if (index > -1) {
          this.commentList.splice(index, 1);
        }
        this.messageService.add({ severity: "success", summary: "Success", detail: "Comment deleted successfully!" });
      },
      error:(err) =>{
        console.warn(`error while deleting essay comment list:${err}`);
        this.messageService.add({ severity: "error", summary: "Error", detail: "An error occurred while deleting comment." });
        this.isLoading = false;
      }
    })
  }

  //Called after submission
  resetForm() {
    this.showAddCommentButton = false;
    this.submitted = false;
    this.newCommentRequest = undefined;
    this.commentForm.reset();
    this.commentForm.clearValidators();
  }

  //Prev & Next Buttons
  traverseSubmissions(order: string){
    if(order === 'Prev' && this.currentIndex > 0){
      this.currentIndex--;
      this.updateSelectedSubmission();
    }
    if(order === 'Next' && this.currentIndex < this.submissions.length-1){
      this.currentIndex++;
      this.updateSelectedSubmission();
    }
    this.listEssayComments();
  }

  sanitizeHtml(html: string | null | undefined): string {
    if (!html) return '';

    let processedHtml = html.replaceAll('&nbsp;', ' ');
    const activeComment = this.commentList.find(c => c.id === this.activeCommentId);
    const text = activeComment?.selectedText.trim();
  
    if (text && text.length > 0) {
      // Escape special regex characters in the comment text
      const escapedText = text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // Regex to match text outside of HTML tags
      const regex = new RegExp(`(?!<[^>]*?)(${escapedText})(?![^<]*?>)`, 'g');
      processedHtml = processedHtml.replace(
        regex,
        `<span class="highlight-comment">$1</span>`
      );
    }
    //Sanitize the final HTML output with DOMPurify
    const sanitized = DOMPurify.sanitize(processedHtml, {
      ALLOWED_TAGS: [
        'b', 'i', 'u', 'p', 'span', 'div', 'br', 'strong', 'em',
        'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'
      ],
      ALLOWED_ATTR: ['style', 'class']
    });
    return sanitized;
  }

   onSaveComment()
  {
    this.isLoading = true;
    if (this.selectedSubmission && this.selectedSubmission.id !== undefined) {
      this.essayPilotService.postEssayReviewRequest({
        pastEssaySubmissionId: this.selectedSubmission.id,
        studentId: this.studentId
      }).subscribe({
        next: (response) => {
          this.messageService.add({ severity: "success", summary: "Success", detail: "Essay review request posted successfully!" });
          this.isLoading = false;
        },
        error: (err) => {
          this.isLoading = false;
          console.warn(`error while posting essay review request:${err}`);
        }
      })
      this.closeReviewDialog.emit();
    } else {
      this.isLoading = false;
      console.error("selectedSubmission is undefined, cannot post essay review request.");
    }
  }

   // private updateSelectedSubmission(index: number): void {
  //   if(this.submissions && this.submissions.length > 0 && index >= 0 && index < this.submissions.length){
  //     this.selectedSubmission = this.submissions[index];
  //     this.currentIndex = index;
  //   }else{
  //     this.selectedSubmission = null;
  //     this.currentIndex = 0;
  //   }
    // this.selectedSubmission = this.submissions.length > 0 ? this.submissions[0] : null;
  // }

  //  openSubmission(response: PastEssaySubmissionResponse, index: number) {
  //   // this.selectedSubmission = response;
  //     this.updateSelectedSubmission(index);
  // }


}
